import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Button from './common/Button';
import Input from './common/Input';
import { productService, categoryService, allergenService, ingredientService } from '../services/api';

type Product = {
  id: number;
  name: string;
  category_id?: number;
  category_name?: string;
  image_path?: string;
  allergens?: { id: number; name: string }[];
  ingredients?: { id: number; name: string }[];
};

type Category = {
  id: number;
  name: string;
  parent_id?: number;
};

type MenuItem = {
  product: Product;
};

export type CustomMenuData = {
  id?: number | string;
  name: string;
  price?: number;
  isVisible: boolean;
  items: MenuItem[];
};

interface Props {
  initialData?: CustomMenuData | null;
  onSave: (data: CustomMenuData) => Promise<void>;
  onCancel: () => void;
}

// ─── Layout ──────────────────────────────────────────────────────────────────

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  height: calc(100vh - 140px);
  min-height: 600px;

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Panel = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const PanelHeader = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  flex-shrink: 0;
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  color: #111827;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 14px 16px;
`;

// ─── Form ─────────────────────────────────────────────────────────────────────

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
`;

const ErrorBox = styled.div`
  background: #fee2e2;
  color: #991b1b;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 14px;
  font-size: 13px;
`;

// ─── Menu items ────────────────────────────────────────────────────────────────

const EmptyPortate = styled.div`
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
`;

const MenuItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  margin-bottom: 8px;
`;

const ArrowCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-shrink: 0;
`;

const ArrowBtn = styled.button`
  background: #f3f4f6;
  border: none;
  border-radius: 4px;
  width: 24px;
  height: 20px;
  cursor: pointer;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #374151;

  &:hover:not(:disabled) { background: #e5e7eb; }
  &:disabled { opacity: 0.3; cursor: default; }
`;

const ItemNum = styled.span`
  font-size: 11px;
  color: #9ca3af;
  font-weight: 600;
  width: 18px;
  text-align: center;
  flex-shrink: 0;
`;

// ─── Catalog ──────────────────────────────────────────────────────────────────

const FiltersRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
`;

const CategorySelect = styled.select`
  flex: 1;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 13px;
  background: white;
  color: #111827;
`;

const ProductList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ProductItem = styled.div<{ $added?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 9px 12px;
  border: 1px solid ${p => p.$added ? '#86efac' : '#e5e7eb'};
  background: ${p => p.$added ? '#f0fdf4' : '#fff'};
  border-radius: 8px;
  transition: all 0.15s;

  &:hover {
    border-color: ${p => p.$added ? '#4ade80' : '#3b82f6'};
  }
`;

const ProductName = styled.span`
  font-weight: 500;
  font-size: 13px;
  color: #111827;
`;

const CategoryBadge = styled.span`
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  background: #f3f4f6;
  color: #6b7280;
  margin-left: 6px;
`;

const AddedMark = styled.span`
  font-size: 11px;
  color: #16a34a;
  font-weight: 600;
  margin-left: 6px;
`;

const ProductActions = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`;

// ─── Product modal ────────────────────────────────────────────────────────────

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 12px;
  width: 92vw;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0,0,0,0.2);
  display: flex;
  flex-direction: column;
`;

const ModalHead = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: #6b7280;
  line-height: 1;
  &:hover { color: #374151; }
`;

const ModalBody = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModalFooter = styled.div`
  padding: 14px 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  display: block;
  margin-bottom: 4px;
`;

const ModalSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: white;
`;

const ImageUpload = styled.div`
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  padding: 14px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover { border-color: #3b82f6; }
`;

const AccordionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  font-size: 13px;
  font-weight: 600;
  color: #374151;

  &:hover { background: #f3f4f6; }
`;

const AccordionArrow = styled.span<{ $open: boolean }>`
  transition: transform 0.2s;
  transform: ${p => p.$open ? 'rotate(0deg)' : 'rotate(-90deg)'};
  color: #6b7280;
  font-size: 11px;
`;

const SelectionBadge = styled.span`
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 999px;
  margin-left: 6px;
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5px;
  max-height: 160px;
  overflow-y: auto;
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-top: none;
  border-radius: 0 0 8px 8px;
`;

const Hint = styled.div`
  font-size: 11px;
  color: #9ca3af;
`;

const PASSWORD_RE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/;

// ─── Component ────────────────────────────────────────────────────────────────

const CustomMenuEditor: React.FC<Props> = ({ initialData, onSave, onCancel }) => {
  const [name, setName] = useState(initialData?.name || '');
  const [price, setPrice] = useState(initialData?.price ? String(initialData.price) : '');
  const [isVisible, setIsVisible] = useState(initialData?.isVisible ?? true);
  const [items, setItems] = useState<MenuItem[]>(initialData?.items || []);

  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [allergens, setAllergens] = useState<{id:number;name:string}[]>([]);
  const [ingredients, setIngredients] = useState<{id:number;name:string}[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ─ Product modal state ─
  const [modalOpen, setModalOpen] = useState(false);
  const [modalProduct, setModalProduct] = useState<Product | null>(null); // null = create
  const [modalName, setModalName] = useState('');
  const [modalCategoryId, setModalCategoryId] = useState('');
  const [modalImageFile, setModalImageFile] = useState<File | null>(null);
  const [modalImagePreview, setModalImagePreview] = useState<string | null>(null);
  const [modalAllergenIds, setModalAllergenIds] = useState<number[]>([]);
  const [modalIngredientIds, setModalIngredientIds] = useState<number[]>([]);
  const [modalIngredientQuery, setModalIngredientQuery] = useState('');
  const [modalAllergenQuery, setModalAllergenQuery] = useState('');
  const [ingredientsOpen, setIngredientsOpen] = useState(false);
  const [allergensOpen, setAllergensOpen] = useState(false);
  const [modalSaving, setModalSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  useEffect(() => {
    setLoadingProducts(true);
    Promise.all([
      productService.getAll(),
      categoryService.getAll(),
      allergenService.getAll(),
      ingredientService.getAll(),
    ]).then(([pr, cr, ar, ir]: any[]) => {
      setProducts(pr?.data?.products || []);
      setCategories(cr?.data?.categories || []);
      setAllergens((ar?.data?.allergens || []).map((x: any) => ({ id: x.id, name: x.name })));
      setIngredients((ir?.data?.ingredients || []).map((x: any) => ({ id: x.id, name: x.name })));
    }).catch(console.error).finally(() => setLoadingProducts(false));
  }, []);

  const isAdded = (productId: number) => items.some(it => it.product.id === productId);

  const filteredProducts = useMemo(() => {
    let list = products;
    if (categoryFilter !== 'all') {
      const filterId = Number(categoryFilter);
      // Include selected category AND all its children
      const relevantIds = new Set(
        categories
          .filter(c => c.id === filterId || c.parent_id === filterId)
          .map(c => c.id)
      );
      list = list.filter(p => p.category_id !== undefined && relevantIds.has(p.category_id));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(p =>
        p.name.toLowerCase().includes(q) ||
        (p.category_name || '').toLowerCase().includes(q)
      );
    }
    return list.slice().sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' }));
  }, [products, searchQuery, categoryFilter, categories]);

  // Build ordered list: parents first, children indented under their parent
  const categoriesForSelect = useMemo(() => {
    const parents = categories.filter(c => !c.parent_id);
    const children = categories.filter(c => !!c.parent_id);
    const result: { id: number; name: string; isChild: boolean }[] = [];
    parents.forEach(parent => {
      result.push({ id: parent.id, name: parent.name, isChild: false });
      children
        .filter(c => c.parent_id === parent.id)
        .sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' }))
        .forEach(child => result.push({ id: child.id, name: child.name, isChild: true }));
    });
    // orphan children (parent not loaded)
    const parentIds = new Set(parents.map(p => p.id));
    children
      .filter(c => !parentIds.has(c.parent_id!))
      .forEach(child => result.push({ id: child.id, name: child.name, isChild: false }));
    return result;
  }, [categories]);

  const handleToggleProduct = (product: Product) => {
    if (isAdded(product.id)) {
      setItems(prev => prev.filter(it => it.product.id !== product.id));
    } else {
      setItems(prev => [...prev, { product }]);
    }
  };

  const handleMoveItem = (index: number, dir: 'up' | 'down') => {
    setItems(prev => {
      const next = [...prev];
      const target = dir === 'up' ? index - 1 : index + 1;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const openCreateModal = () => {
    setModalProduct(null);
    setModalName('');
    setModalCategoryId('');
    setModalImageFile(null);
    setModalImagePreview(null);
    setModalAllergenIds([]);
    setModalIngredientIds([]);
    setModalIngredientQuery('');
    setModalAllergenQuery('');
    setIngredientsOpen(false);
    setAllergensOpen(false);
    setModalError(null);
    setModalOpen(true);
  };

  const openEditModal = async (product: Product) => {
    setModalProduct(product);
    setModalName(product.name);
    setModalCategoryId(product.category_id ? String(product.category_id) : '');
    setModalImageFile(null);
    setModalImagePreview(product.image_path || null);
    setModalIngredientQuery('');
    setModalAllergenQuery('');
    setModalError(null);
    setIngredientsOpen(false);
    setAllergensOpen(false);
    setModalOpen(true);
    try {
      const res: any = await productService.getById(product.id);
      const p = res?.data?.product || res?.product || res;
      setModalAllergenIds(Array.isArray(p?.allergens) ? p.allergens.map((x: any) => x.id) : []);
      setModalIngredientIds(Array.isArray(p?.ingredients) ? p.ingredients.map((x: any) => x.id) : []);
      setIngredientsOpen((p?.ingredients?.length || 0) > 0);
      setAllergensOpen((p?.allergens?.length || 0) > 0);
    } catch {
      setModalAllergenIds([]);
      setModalIngredientIds([]);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalProduct(null);
  };

  const handleModalImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setModalError('Seleziona un file immagine valido'); return; }
    if (file.size > 5 * 1024 * 1024) { setModalError("L'immagine deve essere inferiore a 5MB"); return; }
    setModalError(null);
    setModalImageFile(file);
    setModalImagePreview(URL.createObjectURL(file));
  };

  const handleModalSave = async () => {
    if (!modalName.trim()) { setModalError('Inserisci il nome del prodotto'); return; }
    setModalSaving(true);
    setModalError(null);
    try {
      const fd = new FormData();
      fd.append('name', modalName.trim());
      if (modalCategoryId) fd.append('category_id', modalCategoryId);
      if (modalImageFile) fd.append('image', modalImageFile);
      modalAllergenIds.forEach(id => fd.append('allergen_ids[]', String(id)));
      modalIngredientIds.forEach(id => fd.append('ingredient_ids[]', String(id)));
      if (!modalProduct) fd.append('is_available', 'false');

      if (modalProduct) {
        const res: any = await productService.update(modalProduct.id, fd);
        const updated: Product = res?.data?.product || res?.product || res;
        setProducts(prev => prev.map(p => p.id === updated.id ? updated : p));
        setItems(prev => prev.map(it => it.product.id === updated.id ? { product: updated } : it));
      } else {
        const res: any = await productService.create(fd);
        const created: Product = res?.data?.product || res?.product || res;
        setProducts(prev => [created, ...prev]);
        setItems(prev => [...prev, { product: created }]);
      }
      closeModal();
    } catch (e: any) {
      setModalError(e?.response?.data?.message || 'Errore durante il salvataggio');
    } finally {
      setModalSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) { setError('Inserisci il nome del menu'); return; }
    if (items.length === 0) { setError('Aggiungi almeno una portata al menu'); return; }
    setSaving(true);
    setError(null);
    try {
      await onSave({ id: initialData?.id, name, price: price ? parseFloat(price) : undefined, isVisible, items });
    } catch {
      setError('Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Container>
        {/* ── Left: menu builder ── */}
        <Panel>
          <PanelHeader>
            <PanelTitle>
              Composizione Menu
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="secondary" size="small" onClick={onCancel}>Annulla</Button>
                <Button size="small" onClick={handleSubmit} disabled={saving}>
                  {saving ? 'Salvataggio...' : 'Salva Menu'}
                </Button>
              </div>
            </PanelTitle>
          </PanelHeader>
          <PanelContent>
            {error && <ErrorBox>{error}</ErrorBox>}

            <FormRow>
              <Input label="Nome Menu *" value={name} onChange={e => setName(e.target.value)} fullWidth />
              <Input label="Prezzo (€)" type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="0.00" fullWidth />
            </FormRow>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, cursor: 'pointer', fontSize: 14 }}>
              <input type="checkbox" checked={isVisible} onChange={e => setIsVisible(e.target.checked)} />
              Visibile nel listino
            </label>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
                Portate {items.length > 0 && <SelectionBadge>{items.length}</SelectionBadge>}
              </span>
            </div>

            {items.length === 0 ? (
              <EmptyPortate>Seleziona i prodotti dalla colonna a destra</EmptyPortate>
            ) : (
              items.map((item, idx) => (
                <MenuItemRow key={`${item.product.id}-${idx}`}>
                  <ArrowCol>
                    <ArrowBtn disabled={idx === 0} onClick={() => handleMoveItem(idx, 'up')}>▲</ArrowBtn>
                    <ArrowBtn disabled={idx === items.length - 1} onClick={() => handleMoveItem(idx, 'down')}>▼</ArrowBtn>
                  </ArrowCol>
                  <ItemNum>{idx + 1}</ItemNum>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{item.product.name}</div>
                    {item.product.category_name && (
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{item.product.category_name}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Button variant="secondary" size="small" onClick={() => openEditModal(item.product)}>
                      Modifica
                    </Button>
                    <Button variant="danger" size="small" onClick={() => handleToggleProduct(item.product)}>
                      Rimuovi
                    </Button>
                  </div>
                </MenuItemRow>
              ))
            )}
          </PanelContent>
        </Panel>

        {/* ── Right: catalog ── */}
        <Panel>
          <PanelHeader>
            <PanelTitle>
              Prodotti
              <Button size="small" onClick={openCreateModal}>+ Crea Nuovo</Button>
            </PanelTitle>
          </PanelHeader>
          <PanelContent>
            <FiltersRow>
              <Input
                placeholder="Cerca prodotto..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                fullWidth
              />
              <CategorySelect value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                <option value="all">Tutte le categorie</option>
                {categoriesForSelect.map(c => (
                  <option key={c.id} value={String(c.id)}>
                    {c.isChild ? `  ↳ ${c.name}` : c.name}
                  </option>
                ))}
              </CategorySelect>
            </FiltersRow>

            {items.length > 0 && (
              <Hint style={{ marginBottom: 8 }}>
                ✓ = già nel menu · clicca "Rimuovi" per toglierlo
              </Hint>
            )}

            {loadingProducts ? (
              <div style={{ color: '#6b7280', textAlign: 'center', padding: 32 }}>Caricamento prodotti...</div>
            ) : filteredProducts.length === 0 ? (
              <div style={{ color: '#9ca3af', textAlign: 'center', padding: 32, fontSize: 14 }}>
                {searchQuery || categoryFilter !== 'all' ? 'Nessun prodotto trovato' : 'Nessun prodotto disponibile'}
              </div>
            ) : (
              <ProductList>
                {filteredProducts.map(p => {
                  const added = isAdded(p.id);
                  return (
                    <ProductItem key={p.id} $added={added}>
                      <div style={{ minWidth: 0 }}>
                        <ProductName>{p.name}</ProductName>
                        {p.category_name && <CategoryBadge>{p.category_name}</CategoryBadge>}
                        {added && <AddedMark>✓</AddedMark>}
                      </div>
                      <ProductActions>
                        <Button
                          size="small"
                          variant="secondary"
                          onClick={() => openEditModal(p)}
                        >
                          Modifica
                        </Button>
                        <Button
                          size="small"
                          variant={added ? 'danger' : 'secondary'}
                          onClick={() => handleToggleProduct(p)}
                        >
                          {added ? 'Rimuovi' : 'Aggiungi'}
                        </Button>
                      </ProductActions>
                    </ProductItem>
                  );
                })}
              </ProductList>
            )}
          </PanelContent>
        </Panel>
      </Container>

      {/* ── Product modal ── */}
      {modalOpen && (
        <ModalBackdrop onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
          <ModalBox>
            <ModalHead>
              <ModalTitle>{modalProduct ? `Modifica: ${modalProduct.name}` : 'Crea Nuovo Prodotto'}</ModalTitle>
              <CloseBtn onClick={closeModal}>×</CloseBtn>
            </ModalHead>
            <ModalBody>
              {modalError && <ErrorBox>{modalError}</ErrorBox>}

              <Input
                label="Nome prodotto *"
                value={modalName}
                onChange={e => setModalName(e.target.value)}
                fullWidth
                autoFocus
              />

              <div>
                <FieldLabel>Categoria</FieldLabel>
                <ModalSelect value={modalCategoryId} onChange={e => setModalCategoryId(e.target.value)}>
                  <option value="">Nessuna categoria</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </ModalSelect>
              </div>

              <div>
                <FieldLabel>Foto prodotto</FieldLabel>
                <input
                  type="file"
                  accept="image/*"
                  id="modal-image-upload"
                  style={{ display: 'none' }}
                  onChange={handleModalImageChange}
                />
                <label htmlFor="modal-image-upload">
                  <ImageUpload>
                    {modalImagePreview ? (
                      <img src={modalImagePreview} alt="Preview" style={{ maxHeight: 100, borderRadius: 6, objectFit: 'contain' }} />
                    ) : (
                      <span style={{ fontSize: 13, color: '#6b7280' }}>📁 Clicca per caricare una foto</span>
                    )}
                  </ImageUpload>
                </label>
              </div>

              {/* Ingredienti accordion */}
              <div>
                <AccordionHeader onClick={() => setIngredientsOpen(o => !o)}>
                  <span>
                    Ingredienti
                    {modalIngredientIds.length > 0 && (
                      <SelectionBadge>{modalIngredientIds.length} selezionati</SelectionBadge>
                    )}
                  </span>
                  <AccordionArrow $open={ingredientsOpen}>▾</AccordionArrow>
                </AccordionHeader>
                {ingredientsOpen && (
                  <>
                    <div style={{ padding: '8px 0 4px' }}>
                      <Input
                        placeholder="Cerca ingrediente..."
                        value={modalIngredientQuery}
                        onChange={e => setModalIngredientQuery(e.target.value)}
                        fullWidth
                      />
                    </div>
                    <CheckboxGrid>
                      {ingredients
                        .filter(i => i.name.toLowerCase().includes(modalIngredientQuery.toLowerCase()))
                        .sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' }))
                        .map(i => (
                          <label key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={modalIngredientIds.includes(i.id)}
                              onChange={e => setModalIngredientIds(prev =>
                                e.target.checked ? [...prev, i.id] : prev.filter(x => x !== i.id)
                              )}
                            />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.name}</span>
                          </label>
                        ))}
                    </CheckboxGrid>
                  </>
                )}
              </div>

              {/* Allergeni accordion */}
              <div>
                <AccordionHeader onClick={() => setAllergensOpen(o => !o)}>
                  <span>
                    Allergeni
                    {modalAllergenIds.length > 0 && (
                      <SelectionBadge>{modalAllergenIds.length} selezionati</SelectionBadge>
                    )}
                  </span>
                  <AccordionArrow $open={allergensOpen}>▾</AccordionArrow>
                </AccordionHeader>
                {allergensOpen && (
                  <>
                    <div style={{ padding: '8px 0 4px' }}>
                      <Input
                        placeholder="Cerca allergene..."
                        value={modalAllergenQuery}
                        onChange={e => setModalAllergenQuery(e.target.value)}
                        fullWidth
                      />
                    </div>
                    <CheckboxGrid>
                      {allergens
                        .filter(a => a.name.toLowerCase().includes(modalAllergenQuery.toLowerCase()))
                        .sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' }))
                        .map(a => (
                          <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={modalAllergenIds.includes(a.id)}
                              onChange={e => setModalAllergenIds(prev =>
                                e.target.checked ? [...prev, a.id] : prev.filter(x => x !== a.id)
                              )}
                            />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                          </label>
                        ))}
                    </CheckboxGrid>
                  </>
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" size="small" onClick={closeModal} disabled={modalSaving}>
                Annulla
              </Button>
              <Button size="small" onClick={handleModalSave} disabled={modalSaving}>
                {modalSaving ? 'Salvataggio...' : (modalProduct ? 'Salva modifiche' : 'Crea e aggiungi al menu')}
              </Button>
            </ModalFooter>
          </ModalBox>
        </ModalBackdrop>
      )}
    </>
  );
};

export default CustomMenuEditor;
